// Custom color palette - Purple/Indigo Theme
const colors = {
  // Primary colors - Main purple theme
  primary: {
    main: '#7C5C94', // Main purple
    light: '#C799F6', // Light purple
    dark: '#5D4873', // Darker purple
    contrastText: '#ffffff',
  },
  // Secondary colors - Supporting purples
  secondary: {
    main: '#745C90', // Secondary purple
    light: '#7C639C', // Lighter secondary
    dark: '#4C3B5C', // Darker secondary
    contrastText: '#ffffff',
  },
  // Success colors - Green with purple tint
  success: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
    contrastText: '#ffffff',
  },
  // Warning colors - Orange with purple tint
  warning: {
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100',
    contrastText: '#ffffff',
  },
  // Error colors - Red with purple tint
  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
    contrastText: '#ffffff',
  },
  // Info colors - Blue with purple tint
  info: {
    main: '#0288d1',
    light: '#03a9f4',
    dark: '#01579b',
    contrastText: '#ffffff',
  },
  // Grey colors - Purple-tinted grays
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// Light mode palette - Purple theme
const lightPalette = {
  background: {
    default: '#fafafa',
    paper: '#ffffff',
    primary: '#ffffff',
    secondary: '#f8f7fa', // Very light purple tint
  },
  text: {
    primary: '#17121C', // Dark purple-black
    secondary: '#5D4873', // Medium purple
    disabled: '#7C639C', // Light purple
    hint: '#745C90', // Medium-light purple
  },
  border: {
    main: '#E8E4F0', // Very light purple border
    secondary: '#D4CCE0', // Light purple border
    hover: '#7C5C94', // Primary purple
  },
  divider: '#E8E4F0', // Very light purple
  action: {
    active: '#5D4873', // Dark purple
    hover: '#F5F3FA', // Very light purple background
    selected: '#E8E4F0', // Light purple background
    disabled: '#D4CCE0', // Light purple
    disabledBackground: '#F5F3FA', // Very light purple
  },
  gradient: {
    primary: 'linear-gradient(135deg, #7C5C94 0%, #C799F6 100%)',
    secondary: 'linear-gradient(135deg, #745C90 0%, #7C639C 100%)',
    accent: 'linear-gradient(135deg, #C799F6 0%, #7C5C94 100%)',
    success: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    warning: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    error: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    background: 'linear-gradient(135deg, #f8f7fa 0%, #E8E4F0 100%)',
    card: 'linear-gradient(135deg, #ffffff 0%, #f8f7fa 100%)',
  },
  scrollbar: {
    track: '#f1f1f1',
    thumb: '#D4CCE0', // Light purple
    thumbHover: '#7C639C', // Medium purple
    corner: '#f1f1f1',
  },
  // Surface colors for overlays, glass effects, and subtle backgrounds
  surface: {
    overlay: 'rgba(124, 92, 148, 0.1)', // Light purple overlay
    subtle: 'rgba(124, 92, 148, 0.08)', // Very subtle purple
    muted: 'rgba(124, 92, 148, 0.06)', // Extremely subtle purple
    glass: 'linear-gradient(45deg, rgba(255,255,255,0.8), rgba(248,247,250,0.6))', // Light glassmorphic effect
  },
  // Accent colors for interactive elements and highlights
  accent: {
    primary: '#7C5C94', // Main purple accent
    secondary: 'rgba(124, 92, 148, 0.6)', // Semi-transparent purple
  },
  // Shadow colors and effects
  shadow: {
    light: '0 2px 8px rgba(0,0,0,0.08)',
    medium: '0 4px 16px rgba(0,0,0,0.12)',
    heavy: '0 8px 32px rgba(0,0,0,0.16)',
    color: 'rgba(0,0,0,0.15)', // Shadow color for drop-shadow filters
  },
};

// Dark mode palette - Purple theme
const darkPalette = {
  background: {
    default: '#0a0a0a', // Much darker background
    paper: '#1a1a1a', // Darker paper background
    primary: '#1a1a1a', // Darker primary background
    secondary: '#2a2a2a', // Darker secondary background
  },
  text: {
    primary: '#ffffff',
    secondary: '#b3b3b3', // Lighter secondary text for better contrast
    disabled: '#666666',
    hint: '#999999',
  },
  border: {
    main: '#333333', // Darker borders
    secondary: '#404040', // Darker secondary borders
    hover: '#C799F6', // Light purple
  },
  divider: '#333333', // Darker dividers
  action: {
    active: '#C799F6', // Light purple
    hover: '#2a2a2a', // Darker hover background
    selected: '#7C5C94', // Primary purple
    disabled: '#404040', // Darker disabled
    disabledBackground: '#1a1a1a', // Darker disabled background
  },
  gradient: {
    primary: 'linear-gradient(135deg, #7C5C94 0%, #C799F6 100%)',
    secondary: 'linear-gradient(135deg, #745C90 0%, #7C639C 100%)',
    accent: 'linear-gradient(135deg, #C799F6 0%, #7C5C94 100%)',
    success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    warning: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    error: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)', // Much darker gradient
    card: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)', // Darker card gradient
  },
  scrollbar: {
    track: '#1a1a1a', // Darker track
    thumb: '#404040', // Darker thumb
    thumbHover: '#5D4873', // Medium purple
    corner: '#1a1a1a', // Darker corner
  },
  // Surface colors for overlays, glass effects, and subtle backgrounds
  surface: {
    overlay: 'rgba(199, 153, 246, 0.15)', // Light purple overlay for dark theme
    subtle: 'rgba(199, 153, 246, 0.12)', // Very subtle purple
    muted: 'rgba(199, 153, 246, 0.08)', // Extremely subtle purple
    glass: 'linear-gradient(45deg, rgba(255,255,255,0.1), rgba(199,153,246,0.05))', // Dark glassmorphic effect
  },
  // Accent colors for interactive elements and highlights
  accent: {
    primary: '#C799F6', // Light purple accent for dark theme
    secondary: 'rgba(199, 153, 246, 0.6)', // Semi-transparent light purple
  },
  // Shadow colors and effects
  shadow: {
    light: '0 2px 8px rgba(0,0,0,0.3)',
    medium: '0 4px 16px rgba(0,0,0,0.4)',
    heavy: '0 8px 32px rgba(0,0,0,0.5)',
    color: 'rgba(0,0,0,0.6)', // Darker shadow color for drop-shadow filters
  },
};

// Typography configuration
const typography = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontSize: '2.5rem',
    fontWeight: 300,
    lineHeight: 1.2,
    letterSpacing: '-0.00833em',
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 300,
    lineHeight: 1.3,
    letterSpacing: '0em',
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 400,
    lineHeight: 1.4,
    letterSpacing: '0.00735em',
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 400,
    lineHeight: 1.4,
    letterSpacing: '0.00735em',
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0em',
  },
  h6: {
    fontSize: '1.125rem',
    fontWeight: 500,
    lineHeight: 1.6,
    letterSpacing: '0.0075em',
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.75,
    letterSpacing: '0.00938em',
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.57,
    letterSpacing: '0.00714em',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.43,
    letterSpacing: '0.01071em',
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: '0.02857em',
    textTransform: 'uppercase',
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: '0.03333em',
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 2.66,
    letterSpacing: '0.08333em',
    textTransform: 'uppercase',
  },
};

// Spacing configuration
const spacing = 8;

// Shadows configuration
const shadows = [
  'none',
  '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
  '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
  '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
  '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
  '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
  '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
  '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
  '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
  '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
  '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
  '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
  '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
  '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
  '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
  '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
  '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
  '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
  '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
  '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
  '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
  '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
  '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
  '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
  '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
];

// Component overrides
const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 500,
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
        },
        '&:disabled': {
          backgroundColor: 'var(--mui-palette-action-disabledBackground)',
          color: 'var(--mui-palette-action-disabled)',
          '&:hover': {
            backgroundColor: 'var(--mui-palette-action-disabledBackground)',
          },
        },
      },
      outlined: {
        '&:disabled': {
          borderColor: 'var(--mui-palette-action-disabled)',
          color: 'var(--mui-palette-action-disabled)',
          backgroundColor: 'transparent',
        },
      },
      text: {
        '&:disabled': {
          color: 'var(--mui-palette-action-disabled)',
          backgroundColor: 'transparent',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0px 1px 3px rgba(0,0,0,0.1)',
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      // Custom scrollbar styles for Webkit browsers (Chrome, Safari, Edge)
      '*::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '*::-webkit-scrollbar-track': {
        background: 'var(--scrollbar-track)',
        borderRadius: '4px',
      },
      '*::-webkit-scrollbar-thumb': {
        background: 'var(--scrollbar-thumb)',
        borderRadius: '4px',
        '&:hover': {
          background: 'var(--scrollbar-thumb-hover)',
        },
      },
      '*::-webkit-scrollbar-corner': {
        background: 'var(--scrollbar-corner)',
      },
      // Custom scrollbar styles for Firefox
      '*': {
        scrollbarWidth: 'thin',
        scrollbarColor: 'var(--scrollbar-thumb) var(--scrollbar-track)',
      },
    },
  },
};

// Main theme configuration
export const themeConfig = {
  palette: {
    ...colors,
    light: lightPalette,
    dark: darkPalette,
  },
  typography,
  spacing,
  shadows,
  components,
  shape: {
    borderRadius: 8,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
};
